/**
 * Evidence Hook
 * Manages Evidence CRUD operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthToken } from './use-auth';

// Types
export type EvidenceType = 
  | 'TEST_RESULTS'
  | 'SECURITY_SCAN'
  | 'PERFORMANCE_TEST'
  | 'CODE_REVIEW'
  | 'MANUAL_QA'
  | 'USER_ACCEPTANCE'
  | 'COMPLIANCE_CHECK';

export interface Evidence {
  id: string;
  projectId: string;
  storyId: string | null;
  type: EvidenceType;
  title: string;
  description: string | null;
  data: Record<string, unknown> | null;
  storagePath: string | null;
  status: 'ACTIVE' | 'ARCHIVED';
  createdAt: string;
  updatedAt: string;
  createdBy: { id: string; name: string | null; email: string };
}

// List evidence
export function useEvidence(projectId: string, filters?: { type?: EvidenceType; storyId?: string }) {
  const token = useAuthToken();
  
  return useQuery({
    queryKey: ['evidence', projectId, filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.type) params.append('type', filters.type);
      if (filters?.storyId) params.append('storyId', filters.storyId);
      
      const response = await fetch(`/api/projects/${projectId}/evidence?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch evidence');
      const data = await response.json();
      return data.data as Evidence[];
    },
    enabled: !!projectId && !!token,
  });
}

// Get single evidence
export function useEvidenceItem(projectId: string, evidenceId: string) {
  const token = useAuthToken();
  
  return useQuery({
    queryKey: ['evidence', projectId, evidenceId],
    queryFn: async () => {
      const response = await fetch(`/api/projects/${projectId}/evidence/${evidenceId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch evidence');
      const data = await response.json();
      return data.data as Evidence;
    },
    enabled: !!projectId && !!evidenceId && !!token,
  });
}

// Create evidence
export function useCreateEvidence(projectId: string) {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: Partial<Evidence> & { type: EvidenceType; title: string }) => {
      const formData = new FormData();
      formData.append('type', data.type);
      formData.append('title', data.title);
      if (data.description) formData.append('description', data.description);
      if (data.storyId) formData.append('storyId', data.storyId);
      if (data.data) formData.append('data', JSON.stringify(data.data));
      
      const response = await fetch(`/api/projects/${projectId}/evidence`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create evidence');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evidence', projectId] });
    },
  });
}

// Update evidence
export function useUpdateEvidence(projectId: string) {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ evidenceId, data }: { evidenceId: string; data: Partial<Evidence> }) => {
      const response = await fetch(`/api/projects/${projectId}/evidence/${evidenceId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update evidence');
      }
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['evidence', projectId] });
      queryClient.invalidateQueries({ queryKey: ['evidence', projectId, variables.evidenceId] });
    },
  });
}

// Update evidence status
export function useUpdateEvidenceStatus(projectId: string) {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ evidenceId, status }: { evidenceId: string; status: Evidence['status'] }) => {
      const response = await fetch(`/api/projects/${projectId}/evidence/${evidenceId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update evidence status');
      }
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['evidence', projectId] });
      queryClient.invalidateQueries({ queryKey: ['evidence', projectId, variables.evidenceId] });
    },
  });
}

// Delete evidence
export function useDeleteEvidence(projectId: string) {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (evidenceId: string) => {
      const response = await fetch(`/api/projects/${projectId}/evidence/${evidenceId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete evidence');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evidence', projectId] });
    },
  });
}
